import { useMutation } from '@tanstack/react-query';
import { Button, Form, Modal } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleTicketV2 } from 'src/apis/helpdesk/ticket.api';
import { OpenHandleType } from 'src/components/v2/Button/BtnHandleList';
import { SolutionTicketSelect, UserAvailableAssignSelect } from 'src/components/v2/Select';
import { TicketDetailContext } from 'src/contexts/ticketDetail.context';
import { showNotification } from 'src/utils/notification';
import { uppercaseFirstLetter } from 'src/utils/utils';

interface Props {
  open: OpenHandleType;
  setOpenHandle: Dispatch<SetStateAction<OpenHandleType>>;
  handleRefresh: () => void;
}

interface HandleType {
  ticketSolutionId?: number | string;
  employeeId?: number;
  remark?: string;
}

export default function HandleTicket({ open, setOpenHandle, handleRefresh }: Props) {
  const [form] = Form.useForm();
  const { ticket } = useContext(TicketDetailContext);
  const { t } = useTranslation();
  const [formState, setFormState] = useState<HandleType>({
    ticketSolutionId: undefined,
    employeeId: undefined
  });

  useEffect(() => {
    setFormState({
      ticketSolutionId: open.solutionTicketId,
      employeeId: open.employeeId
    });
  }, [open]);

  const handleSubmit = () => {
    form.validateFields().then((value) => {
      handleTicketToMutation.mutate(formState, {
        onSuccess: (data) => {
          showNotification(`${t('notification.success.handle')} - ${open.ticketCode}`, 'success');
          setOpenHandle({
            isOpen: false
          });
          setFormState({ ticketSolutionId: undefined, employeeId: undefined });
          form.resetFields();
          handleRefresh();
        }
      });
    });
  };

  const handleCancel = () => {
    setOpenHandle({
      isOpen: false
    });
    form.resetFields();
  };

  const handleTicketToMutation = useMutation({
    mutationFn: (handle: HandleType) =>
      handleTicketV2(
        open.ticketId as string,
        handle.employeeId as number,
        handle.ticketSolutionId as number,
        formState?.remark ? uppercaseFirstLetter(String(formState?.remark)) : ''
      )
  });

  return (
    <Modal
      title={`Xử lý - ${open.ticketCode}`}
      centered
      maskClosable={false}
      open={open.isOpen}
      width={500}
      onOk={handleSubmit}
      onCancel={handleCancel}
      footer={[
        <Button key='back' onClick={handleCancel}>
          {t('action.cancel')}
        </Button>,
        <Button key='submit' type='primary' onClick={handleSubmit} loading={handleTicketToMutation.isLoading}>
          {t('action.handle')}
        </Button>
      ]}
    >
      <Form
        form={form}
        layout='vertical'
        fields={[
          {
            value: formState.employeeId,
            name: 'handler'
          },
          {
            value: formState.ticketSolutionId,
            name: 'ticketSolutionId'
          },
          {
            name: 'remark',
            value: formState?.remark ? uppercaseFirstLetter(String(formState?.remark)) : ''
          }
        ]}
      >
        {ticket?.data.permissionAssign && (
          <Form.Item
            label={'Chỉ định người xử lý'}
            name='handler'
            rules={[{ required: true, message: t('required.ticket.handler').toString() }]}
          >
            <UserAvailableAssignSelect
              departmentId={open?.departmentId}
              ticketCategory={ticket.data.ticketCategory.id}
              placeholder={t('placeholder.ticket.handler')}
              onChange={(value) =>
                setFormState({
                  ...formState,
                  employeeId: value ? Number(value) : undefined
                })
              }
            />
          </Form.Item>
        )}
        {ticket?.data.permissionApproveSolution && (
          <Form.Item label={'Phê duyệt giải pháp'} name='ticketSolutionId'>
            <SolutionTicketSelect
              ticketId={open.ticketId as string}
              placeholder={t('placeholder.ticket.ticketSolution')}
              onChange={(value) =>
                setFormState({
                  ...formState,
                  ticketSolutionId: value ? value : undefined
                })
              }
            />
          </Form.Item>
        )}
        <Form.Item label={t('columnTitle.ticket.remark')} name='remark'>
          <TextArea
            placeholder={t('placeholder.ticket.remark').toString()}
            onChange={(e) =>
              setFormState({
                ...formState,
                remark: e.target.value
              })
            }
            rows={2}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
